import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function Economics37() {

   const contentRef = useRef(null);
         const links = {
        prev: "/economics-36", 
        index: "/index-economics",                 
        next: "/economics-38", 
      };
        const titleText = "The Economic Rationale of Ownership and Property Rights";

        const questions = [
  {
    text: "1. Why do humans feel the need to own property, according to the lesson?",
    options: [
      { value: "A", label: "A. To gain social status" },
      { value: "B", label: "B. To avoid taxation" },
      {
        value: "C",
        label:
          "C. To have control over scarce goods and reuse them when needed",
      },
      { value: "D", label: "D. To increase government regulation" },
    ],
    correct: "C",
  },
  {
    text: "2. Why does private property help preserve resources in the long term?",
    options: [
      { value: "A", label: "A. Because it allows sharing among people" },
      {
        value: "B",
        label: "B. Because owners expect a reward from the government",
      },
      {
        value: "C",
        label:
          "C. Because it gives owners a personal incentive to maintain and reuse their goods",
      },
      {
        value: "D",
        label: "D. Because it reduces the need for economic planning",
      },
    ],
    correct: "C",
  },
  {
    text: "3. Why is the act of producing a durable good considered a sacrifice of present satisfaction?",
    options: [
      {
        value: "A",
        label: "A. Because it always requires monetary investment",
      },
      {
        value: "B",
        label: "B. Because it delays the use of natural resources",
      },
      { value: "C", label: "C. Because it uses up consumer goods" },
      {
        value: "D",
        label:
          "D. Because it involves forgoing immediate consumption to gain future utility",
      },
    ],
    correct: "D",
  },
  {
    text: "4. What happens when economic goods do not have clear ownership rights?",
    options: [
      {
        value: "A",
        label: "A. They are better preserved by the community",
      },
      { value: "B", label: "B. People are less likely to use them" },
      {
        value: "C",
        label:
          "C. Individuals prioritize future utility over present consumption",
      },
      {
        value: "D",
        label:
          "D. Users exploit them without considering future consequences",
      },
    ],
    correct: "D",
  },
];

// State to track selected answers for all questions
      const [answers, setAnswers] = useState({});
    
      // Function to update an answer
      const handleAnswerChange = (questionIndex, selectedValue) => {
        setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
      };
    
      // Compute score
      const score = questions.reduce((total, q, index) => {
        return total + (answers[index] === q.correct ? 1 : 0);
      }, 0);
 
  return (
    <div className="container">
      <LandingSection
              titleText={titleText}
              scrollToRef={contentRef}
              progressValue={37}
              timerMinutes={60}
              currentLevel={2}
              unlockedBadges={3}
            />

      <p className="content" ref={contentRef}>
        Welcome to <b>Day 37</b>!
Today’s lesson explores the deep connection between scarcity and private property, showing why ownership is not an arbitrary social rule but a necessary response to limited resources. We will examine how clearly defined property rights encourage responsibility, preservation, and long-term planning, while their absence leads to waste, conflict, and economic decline. By understanding why private property emerges naturally in a world of scarcity, you will see how it forms one of the essential foundations of a prosperous and orderly society.
<br/><br/>
        Scarcity lies at the heart of economics. It is the foundational concept
        that gives rise to the entire discipline. Scarcity refers to the
        mismatch between the desired quantity of a good and its available
        quantity. Humans desire many things, but resources are finite. It is
        this limitation that forces individuals, communities, and nations to
        make choices. These choices reflect trade-offs, where opting for one
        alternative means forgoing another. Scarcity compels human beings to
        prioritize, to plan, and to conserve. For instance, when a good is
        scarce, people are incentivized to use it carefully, reuse it when
        possible, and consider its long-term utility. This is the primary reason
        behind the evolution of the concept of property. Ownership of scarce
        goods gives individuals a framework to manage their use and protect them
        for future needs. Consider a hunter who crafts a spear. This spear is
        not a disposable item for one-time use. It represents a durable good.
        The hunter will use it repeatedly across many hunts, conserving his
        labor, time, and resources. In crafting that spear, the hunter invests
        present effort for future benefit. This future-oriented behavior is the
        essence of capital formation. It reflects an understanding that present
        sacrifices can generate extended streams of future utility. Scarcity,
        therefore, not only creates value but also fosters innovation, prudence,
        and forward-thinking. Without scarcity, there would be no need for
        economizing, planning, or saving. Economics as a discipline would not
        exist.
        <br />
        The recognition of scarcity leads to the moral and legal institution of
        private property. Ownership emerges not simply as a legal convenience,
        but as a solution to the problem of rivalrous use of scarce resources.
        In a world of scarcity, conflict is inevitable if there is no clear
        system determining who has the right to use a resource. If a fishing
        boat, for example, belongs to no one in particular, then any individual
        could use it at will. However, no one would feel responsible for
        maintaining it, repairing it, or improving it. The absence of ownership
        would quickly degrade its quality and availability. Property rights
        solve this problem by assigning responsibility along with privilege. The
        individual who owns the boat is responsible for its upkeep and benefits
        directly from its proper maintenance. This feedback loop aligns
        incentives in a way that encourages stewardship and long-term thinking.
        It is not merely about possession; it is about responsibility and
        rational allocation under constraints. As such, property rights are not
        just social conventions. They are tools that enhance economic efficiency
        and reduce wasteful conflict. They provide the necessary framework
        within which scarce resources can be employed to their maximum benefit
        for both individuals and society at large.
        <br />
        <br />
        <b>The Nature and Classification of Property</b>
        <br />
        <br />
        Property, in economic terms, refers to the ownership and control of
        goods. These goods can be broadly categorized into three types:
        consumption goods, producer goods, and monetary goods. Consumption goods
        are those that are directly used for the satisfaction of human wants.
        They are often durable in nature, meaning they provide utility over an
        extended period. A house, for example, is a consumption good. It
        shelters a family for many years and requires long-term maintenance and
        care. Durable goods reduce the need for repeated labor, as they allow
        one-time investments to yield continuous returns. When a person builds
        or acquires such a good, they are essentially making a decision to delay
        immediate consumption in favor of securing prolonged utility. This
        decision is fundamentally shaped by their time preference. People with a
        lower time preference are willing to delay gratification. They save and
        invest in goods that offer long-term benefits. High time preference
        individuals, on the other hand, prioritize immediate consumption and may
        neglect the importance of durable consumption goods. The economic
        progress of a society depends largely on the average time preference of
        its members. Societies that value saving and future consumption tend to
        accumulate capital and build lasting institutions. Those that consume
        immediately without regard for the future often remain stagnant or
        regress.
        <br />
        Producer goods, on the other hand, are not directly consumed but are
        used in the production of other goods. They include tools, machines,
        factories, raw materials, and technology. For example, a lathe machine
        in a workshop is a producer good. It does not satisfy any human need by
        itself, but it enables the production of items that do. The accumulation
        and improvement of producer goods are what lead to productivity gains in
        an economy. The more advanced the tools, the more output a worker can
        produce with the same effort. This increase in productivity is what
        drives economic growth. It is important to understand that producer
        goods are also subject to the laws of scarcity and ownership. Just like
        consumption goods, they must be maintained and used efficiently. When
        ownership is unclear or public, these tools are often neglected. In a
        factory where machines are owned collectively but without defined
        responsibility, they are used carelessly, maintenance is delayed, and
        output suffers. But when machines are privately owned, the owner has a
        direct interest in their longevity and efficiency. Ownership therefore
        instills a culture of care, discipline, and improvement. Finally,
        monetary goods serve as a medium of exchange. They do not directly
        satisfy human wants or produce other goods, but they make trade easier.
        In a modern economy, money serves as a universal intermediary. It allows
        people to convert their labor and products into any other good available
        in the market. Money is also a store of value and a unit of account. For
        money to serve these functions reliably, it must be scarce and durable.
        Any system of property and trade must include a sound monetary system
        that preserves the integrity of exchange and savings.
        <br />
        <br />
        <b>Why Ownership Encourages Responsibility and Preservation</b>
        <br />
        <br />
        The importance of ownership becomes even more pronounced when examining
        what happens in its absence. When economic goods are not owned, or when
        ownership rights are unclear or unenforced, these goods are often
        depleted or destroyed. This phenomenon is known as the tragedy of the
        commons. Imagine a piece of grazing land that belongs to no one in
        particular. Each herder has an incentive to send as many animals as
        possible to graze there, because the costs of overgrazing are shared by
        all, but the benefits of extra grazing accrue to the individual.
        Eventually, the land becomes barren, and everyone suffers. This example
        illustrates what happens when there is no clear ownership and no system
        of responsibility. The resource is treated as if it were infinite, which
        it is not. In contrast, when the same land is privately owned, the owner
        will limit grazing to sustainable levels, invest in improving the soil,
        and ensure long-term productivity. The owner has a vested interest in
        the future value of the land. This fundamental difference in behavior is
        not about morality but about incentives. Ownership aligns incentives
        with long-term preservation. When people expect to benefit from a
        resource in the future, they are more likely to treat it carefully in
        the present. This applies not only to land but to all types of property.
        Whether it is a tool, a building, a brand, or a piece of software,
        ownership encourages care and forward thinking.
        <br />
        The lack of ownership does more than just invite short-term thinking. It
        often leads to outright destruction. Resources that are abundant and
        free for the taking are often wasted. Forests without private ownership
        are cut down recklessly. Rivers without responsible oversight are
        polluted. In each of these cases, individuals are not trying to be
        malicious. They are responding to incentives in an environment where no
        one is responsible for the long-term health of the resource. Private
        property creates the opposite environment. It fosters the expectation
        that a resource must be maintained, improved, and passed on in good
        condition. It fosters stewardship and innovation. For example, a farmer
        who owns his land will invest in soil improvement, irrigation, and crop
        rotation. A tenant farmer, uncertain of his future on the land, may
        exhaust the soil for short-term gain. This principle holds true in urban
        settings as well. A tenant in a rental apartment may neglect minor
        maintenance issues that a homeowner would address promptly. The
        difference is not about virtue. It is about incentive structures shaped
        by ownership. Furthermore, ownership reduces conflict. When rights are
        clearly defined and legally protected, people can resolve disputes
        peacefully through negotiation or legal action. But when ownership is
        ambiguous, disputes are more likely to turn violent. Property rights
        provide a framework for cooperation and peaceful coexistence. They
        enable specialization, trade, and social harmony.
        <br />
        the economics of scarcity leads directly to the necessity and logic of
        property ownership. Without scarcity, there would be no need to
        prioritize. Without ownership, there would be no reason to conserve.
        Scarcity motivates careful planning. Ownership facilitates efficient
        allocation. The interplay between these two concepts forms the bedrock
        of a functioning economy. Property rights are not just about wealth or
        possession. They are about aligning incentives for the long-term benefit
        of individuals and societies. The more secure and widespread property
        rights are, the more prosperous and harmonious the society becomes. They
        reduce waste, prevent conflict, and promote productivity. In a world of
        scarce resources, nothing could be more important.
        <br />
        <br />
        <b>The Case for Private Property</b>
        <br />
        <br />
        Those who deny the legitimacy of private property rights fundamentally
        misunderstand both the nature of scarcity and the incentives required
        for a functional economy. Property is not merely a social construct but
        a logical necessity that emerges from the reality of scarce resources.
        Without clearly defined ownership, goods are subject to overuse,
        neglect, and conflict, as no individual bears responsibility for their
        preservation or productivity. This tragedy of the commons leads to
        short-term consumption at the expense of long-term sustainability. The
        institution of private property is inseparable from civilization’s
        progress, savings, capital formation, and technological advancement.
        When property is denied or collectivized, time preference rises and
        people are disincentivized from thinking about the future, leading to
        economic stagnation and social decay. Refusing property rights is thus
        not just a theoretical error but a practical path to impoverishment.
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        Today’s lesson explored the concept of scarcity as the foundational
        principle of economics. It explained how the mismatch between the
        desired and available quantity of goods forces humans to be careful and
        intentional in their use of resources. The need for private property
        arises from this scarcity, as it allows individuals to manage, preserve,
        and reuse goods over time. The lesson examined the benefits of producing
        durable goods that provide long-term utility, even at the cost of
        sacrificing short-term consumption. It also categorized property into
        three main types: consumer goods for durable consumption, producer goods
        used in production, and monetary goods used for exchange. A central
        theme was that private ownership leads to better preservation of
        resources because the owner has a vested interest in their long-term
        utility, while the lack of ownership causes individuals to exploit
        resources without concern for their future condition.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        In our next lesson, we’ll explore a specific and highly debated form of
        government price intervention—rent control. Often introduced during
        times of housing crises, wars, or rapid urbanization, rent control is
        presented as a way to protect tenants from rising housing costs. But
        does it really help those in need, or does it deepen the housing
        shortage and degrade the quality of available accommodations? Join us on
        Day 38 as we unpack the economic distortions and long-term consequences
        of rent control policies.
      </p>
      <h2>Test your understanding</h2>
     {questions.map((q, index) => (
      <Question
        key={index}
        questionText={q.text}
        options={q.options}
        correctAnswer={q.correct}
        onAnswer={(value) => handleAnswerChange(index, value)} // pass parent handler
      />
    ))}
    
           <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
            Score: {score} / {questions.length}
          </div>
   
      <FloatingPageNav 
              prevLink={links.prev} 
              indexLink={links.index} 
              nextLink={links.next} 
            />
    </div>
  );
}

export default Economics37;
